import {
	BufferAttribute,
	BufferGeometry,
	DoubleSide,
	Group,
	Mesh,
	MeshBasicMaterial,
	Quaternion,
	Vector2,
	Vector3,
} from 'three';
import Planet4 from 'imperial-planet/next';       // 4th-gen renderer: worker gen + GPU bakes, self-lit
import LegacyPlanet from 'imperial-planet/legacy'; // 3rd-gen renderer: sync mesh, scene-lit (sun PointLight)
const PLANET_EXPOSURE = 0.8; // the game's base planet exposure (the XR EXPOSURE setting multiplies it)
import Settlement from './settlement.js';
import { MeshLine, MeshLineMaterial } from './three.meshline.js';
import { createLabel } from './labels.js';
import { gpuGrade } from './gpu-grade.js';

// Planet — game wrapper over ONE of the two imperial-planet renderers. Both
// implement the same Planet contract (build/object3d/planetMesh/tiles/dispose);
// the wrapper hides which one is active behind this.tiles/faceIndex/$next so
// sector-map (picking, hover outlines, LOD, cache) works with either.
//
// TWO RENDERERS, MAINTAINED IN PARALLEL (decision 2026-09-28):
//   next   — desktop + XR headsets. Heavy (per-planet array render-targets),
//            tiered by zoom (sector-map planetTier).
//   legacy — PHONES/TABLETS. `next`'s fragment shader faults the Adreno 6xx
//            driver on Android (Moto G84: GPU process dies 77–114 s after ANY
//            next planet is resident — baked or simple, one planet or six;
//            see STATUS 2026-07-23). legacy is the renderer that shipped on
//            phones for months without a fault. No tiers: one mesh per planet.
// Override on any device (testing / forcing a path): URL `?planet=legacy|next`
// wins, then localStorage `planetRenderer`, then the UA default below.
const HEADSET_UA = /Pico|Quest|OculusBrowser/i;
const SETTLEMENT_BORDER_HALF_WIDTH = 0.0015; // headset ribbon border, unit-sphere units (planet radius = 1)
// The renderer outputs LINEAR colour (miserit-anthar.js outputColorSpace = LinearSRGBColorSpace), so a
// material colour is shown ~squared: 0x0000bb came out as (0,0,90), a near-black navy — "moving black
// pixels" in the border as the planet turned (user 2026-10-16). Picked by measurement from his camera.
const SETTLEMENT_BORDER_COLOR = 0x2255ff;
// Close-up atlas on headsets: 1024 = the lab's close-planet value (labs/planet-4rdgen-2
// viz/planet.js ARES default). Measured in VR on the Pico 2026-10-10: a size-20 planet
// builds grey→close-up at 1024 in ~4 s (512: ~3 s), no errors; 512 left the big planets
// visibly blurrier than small ones (same texels over 16× the surface). `?hares=` overrides.
const HEADSET_HIGH_ARES = (typeof location !== 'undefined' && Number(new URLSearchParams(location.search).get('hares'))) || 1024;
const PHONE_UA = /Android|iPhone|iPad|iPod|Mobile/i;
function choosePlanetRenderer() {
	let pick = null;
	try { pick = new URLSearchParams(location.search).get('planet'); } catch { /* no location */ }
	if (!pick) try { pick = localStorage.getItem('planetRenderer'); } catch { /* no storage */ }
	if (pick === 'legacy' || pick === 'next') return pick;
	const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
	if (HEADSET_UA.test(ua)) return 'next';   // standalone VR: next's XR/low preset
	if (PHONE_UA.test(ua)) return 'legacy';   // phones/tablets: Adreno-safe
	return 'next';                            // desktop
}
export const PLANET_RENDERER = choosePlanetRenderer();
if (typeof window !== 'undefined') window.__maRenderer = PLANET_RENDERER; // bundle-badge telemetry (instances.js)

// 'g' cycles the hex grid on all planets: semi-transparent black (default) →
// full black → hidden → repeat. (ignored while typing in a form)
const planetInstances = new Set();   // live Planet wrappers
const GRID_STATES = [
	{ visible: true, opacity: 0.2 },  // default: rgba(0,0,0,0.2)
	{ visible: true, opacity: 1.0 },  // full black
	{ visible: false, opacity: 1.0 }, // hidden
];
let gridState = 0;
function applyGridState(inst) {
	const s = GRID_STATES[gridState];
	const r = inst.$next || inst.$legacy;
	if (!r) return;
	r.setGridVisible(s.visible);
	// legacy's grid is a fixed-opacity LineSegments (no setGridOpacity) —
	// for it the cycle degrades to visible → visible → hidden.
	if (s.visible && r.setGridOpacity) r.setGridOpacity(s.opacity);
}
const SETTLEMENT_UP = new Vector3(0, 0, 1);
const _sunDir = new Vector3();     // scratch: per-frame planet→star direction
const _sunQuat = new Quaternion(); // scratch: planet world rotation for lighting
if (typeof addEventListener === 'function' && typeof document !== 'undefined') {
	addEventListener('keydown', e => {
		if (e.key !== 'g' || /INPUT|TEXTAREA/.test(e.target?.tagName || '')) return;
		gridState = (gridState + 1) % GRID_STATES.length;
		for (const inst of planetInstances) applyGridState(inst);
	});
}

export default class Planet {
	constructor(planet, opts = {}) {
		this.planet = planet;
		this.system = planet.system;
		// quality tier chosen by the caller (sector-map's zoom-based LOD):
		// 'low' | 'medium' | 'high'. Absent → derived from the device below.
		this._tier = opts.tier || null;

		this.mesh = new Group();
		this.units = new Group();
		this.settlements = new Group();
		this.facilities = new Group();
		this.settlementLabels = [];
		this.settlementLabelIds = [];
		this.mesh.add(this.units);
	}

	async create() {
		window.__maBuilds = (window.__maBuilds || 0) + 1; // dev telemetry (instances.js ticker)
		if (PLANET_RENDERER === 'legacy') return this._createLegacy();
		return this._createNext();
	}

	// ── legacy (3rd-gen) path: phones/tablets ────────────────────────────────
	// Synchronous build (no worker, no bakes), ONE mesh + the shared terrain.jpg
	// atlas per planet, lit by the system sun's PointLight (sun-shader.js). The
	// builder scales its group to the game radius itself. No quality tier: the
	// tier sector-map passes in is ignored (planetTier() returns the constant
	// 'legacy' when this renderer is active, so LOD never rebuilds).
	async _createLegacy() {
		const p = new LegacyPlanet(
			{
				seed: this.planet.seed ?? this.planet.name,
				type: this.planet.type,
				size: this.planet.size,
				patches: this.planet.patches,
			},
			{ atlasUrl: '/assets/gfx/planet/shader-assets/terrain.jpg' },
		);
		await p.build();
		this.$legacy = p;
		// NOT setting this.$next: sector-map's pick paths branch on it —
		// `data.$next ? tileAtPoint(point) : data.faceIndex[hit.faceIndex]`.
		this.faceIndex = p.faceIndex; // per-triangle → tile index

		const group = p.object3d; // already scaled to ceil(size/5) by the builder
		this.surfaceScale = group.scale.x;
		this.planetMesh = p.planetMesh;
		this.planetMesh.data = this;
		this.planetMesh.userData.noLabelOcclusion = true; // 175k+ tris — core sphere occludes labels instead
		this.atmosphere = p.atmosphere;

		group.add(this.settlements);
		group.add(this.facilities);
		this.mesh.add(group);
		planetInstances.add(this);
		applyGridState(this);

		// tile data in mesh-local units (draped centers, draped boundaries) — the
		// same shape the next path builds; settlements/borders/hover read it.
		const n = p.tileCount;
		const centers = new Array(n);
		for (let i = 0; i < n; i++) centers[i] = p.getTileCenter(i).toArray();
		this.tiles = { centers, drapedCenters: centers, boundaries: p.boundaries, neighbors: p.neighbors };

		await this.createSettlements();
		return this;
	}

	// ── next (4th-gen) path: desktop + XR ────────────────────────────────────
	async _createNext() {
		const gameRadius = Math.ceil(this.planet.size / 5);
		// Quality tier. sector-map picks it from the zoom LOD (medium for a whole
		// sector overview, high only for the zoomed-in handful; mobile gets low/
		// medium). XR ALWAYS forces low — the headset compositor can't take the
		// heavy bakes regardless of zoom. Absent tier (direct construction) falls
		// back to the old UA guess so nothing that skips sector-map crashes mobile.
		const isXR = typeof game !== 'undefined' && game.renderer?.xr?.isPresenting;
		const ua = typeof navigator !== 'undefined' ? (navigator.userAgent || '') : '';
		// HEADSETS (Pico/Quest): ALWAYS the lab's Pico-verified bundle — quality
		// 'low' = the lib's XR_PRESET (baked, bres/mres/sres 256/64/256, wave 0,
		// 8 breakup octaves, 1 bake chunk per frame; labs/planet-4rdgen-2/viz/
		// planet.js `standaloneXR`, lib src/next/index.js XR_PRESET table). The
		// only per-planet knob is `ares` (lab v53: the atlas res IS the per-planet
		// LOD): sector-map's headset tier 'high' = the ONE approached planet →
		// 1024 (the lab's close-planet value), everything else 256.
		// Headsets are NOT phones: the Moto Adreno mitigation below (simple mode,
		// 10 charts, 6 octaves) matched Pico UAs until 2026-09-28 and gutted the
		// headset planets.
		const headsetUA = HEADSET_UA.test(ua);
		const mobileUA = !headsetUA && /Android|Mobile|iPhone|iPad|iPod|Silk/i.test(ua);
		const btileParam = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('btile') : null;
		const bakeSliceParam = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('bakeslice') : null; // imperial-planet bakeSlice: ?bakeslice=1 | =<tileSize>x<tilesPerFrame>
		const bakeTile = btileParam != null ? Number(btileParam) : ((!headsetUA && !mobileUA && gpuGrade().grade === 'weak') ? 128 : 0);
		const tier = (headsetUA || isXR) ? 'low' : (this._tier || (mobileUA ? 'medium' : 'high'));
		// bakes drained per frame: XR/low 1 (fragile compositor), medium 4 (a whole
		// sector of them can be draining at once — keep the per-frame cost bounded),
		// high 16 (few of them, drain fast so detail snaps in).
		// Mobile is ALWAYS 1: at 4/frame the Adreno driver faulted mid-build wave
		// (KGSL EDEADLK → GPU process death in Firefox, WebGL context loss in
		// Chrome — reproduced on the Moto G84 in BOTH, 2026-07-23).
		const chunksPerFrame = (isXR || mobileUA || headsetUA) ? 1 : tier === 'medium' ? 4 : 16;
		// the new 4th-gen renderer: async (spins a worker + streams GPU bakes),
		// needs the host's renderer, and lights itself via a uSunDir uniform.
		const p = new Planet4(
			{
				seed: this.planet.seed ?? this.planet.name,
				type: this.planet.type,
				size: this.planet.size,
				patches: this.planet.patches,
			},
			{
				renderer: game.renderer,
				quality: tier,
				atmosphere: true,
				chunksPerFrame,
				// weak desktop GPUs (client/gpu-grade.js): bake in 128 px tiles so no single draw
				// runs long — Adelheid's Intel reset its context on a full-layer bake ("Resetting
				// rcs0 for preemption time out", 2026-10-11). `?btile=N` overrides (0 = off).
				...(bakeTile ? { btile: bakeTile } : {}),
				// weak desktop GPU at the high tier (the chip is weak but the tier is high: forced ?gpu=strong, which only changes the ladder, not the chip): slice the bakes into short
				// draws — unsliced they hit Adelheid's Intel preemption timeout 2 of 2 runs (2026-10-23). The
				// ?bakeslice= param wins either way (0 = off, 1 / TxN = on).
				...(bakeSliceParam == null && !headsetUA && !mobileUA && tier === 'high' && gpuGrade().real === 'weak' ? { bakeSlice: true } : {}),
				...(bakeSliceParam && bakeSliceParam !== '0' ? { bakeSlice: bakeSliceParam.includes('x') ? { tileSize: +bakeSliceParam.split('x')[0], maxTilesPerFrame: +bakeSliceParam.split('x')[1] } : true } : {}),
				// Phones: unified memory — every GPU byte is a RAM byte Firefox/lmkd
				// will kill for. Shrink the bake targets below the presets and cap
				// the init-worker pool at 2 (6 workers × a parsed three module + 6
				// simultaneous first-compiles of the huge planet shaders spike hard).
				...(mobileUA ? {
					workerPoolSize: 2,
					// SIMPLE mode: no baked material array-RTs, no breakup, no live
					// erosion — the minimal live-shaded pipeline. The full baked
					// pipeline faults the Moto G84's Adreno driver in BOTH browsers
					// (KGSL EDEADLK; Firefox GPU process dies, Chrome loses the WebGL
					// context) even fully serialized at 1 bake/frame (2026-07-23).
					simple: true,
					// the material-bake shader defaults to 12 noise octaves ("one-time,
					// affords full") — on Adreno a full-layer bake draw at 12 octaves is
					// heavy enough to contribute to the driver-fault budget. Phones trade
					// texture detail for staying alive.
					matBakeOctaves: 6,
					// maxCharts caps NCC, which scales EVERYTHING the Adreno fault
					// tracks with: shader uniform-array/loop sizes (FRAG_A/MAT/D),
					// the per-planet array-RT layer count (2×NCC binds+clears+bakes),
					// and the atlas footprint. 24-chart builds faulted the driver
					// even fully serialized at 1 bake/frame; phones cap at 10.
					maxCharts: 10,
					...(tier === 'medium' ? { bres: 256, ares: 384 } : {}),
				} : {}),
				// headset: lab bundle + per-planet atlas LOD (see above); 2 init
				// workers (the lab had one planet = one worker; a 6-worker pool each
				// parsing three is CPU the Pico compositor feels).
				...(headsetUA ? {
					workerPoolSize: 2,
					// Builds happen LIVE while flying (threshold crossings, XR entry),
					// not behind the lab's entry spinner. Atlas: v48's 512 for the
					// approached planet ("reveal transition 1069→250 ms, quality
					// holds", captures/2026-07-18_ares512_*), 256 for the rest. NO
					// bake tiling (btile): at 256/512 a full layer bake is a few ms
					// (lab fence timing: 512² tile <14 ms; the 764 ms stall was a 1024²
					// mountain layer), while tiling ×16 the task count and the headset
					// drains ONE task per frame → a planet took many seconds to
					// un-grey (user, 2026-09-28).
					// DEV knob ?hares=N overrides the close-up atlas (device cost runs,
					// tests/device/lisbeth-cdp/picoares.cjs); the lab's close planet was 1024.
					ares: this._tier === 'high' ? HEADSET_HIGH_ARES : 256,
				} : {}),
				// the game's hex grid is BLACK; 'g' cycles 0.2 → 1.0 → hidden.
				gridColor: 0x000000,
				gridOpacity: 0.2,
				// LAVA samples this atlas (cell 10) for the molten texture; the
				// game serves it here (next's default path is the standalone's).
				atlasUrl: '/assets/gfx/planet/shader-assets/terrain.jpg',
				// game reads brighter than the standalone — dial exposure down a bit.
				exposure: PLANET_EXPOSURE,
				// the game is esbuild-bundled → the worker + its three come from
				// served URLs, not import.meta.url (which points inside the bundle).
				workerUrl: '/imperial-planet/src/next/planet-init-worker.js',
				workerThreeUrl: '/three/build/three.module.js',
			},
		);
		if (typeof window !== 'undefined' && window.__tierLog) window.__tierLog.push({ t: Math.round(performance.now()), event: 'phase:build-call', planet: this.planet.name });
		await p.build();
		if (typeof window !== 'undefined' && window.__tierLog) window.__tierLog.push({ t: Math.round(performance.now()), event: 'phase:build-returned', planet: this.planet.name });
		this.$next = p;

		// next builds a UNIT-radius sphere; scale its group to the game radius so
		// tile coords (unit space) map exactly like legacy's NOISE_RADIUS group did.
		const group = p.object3d;
		group.scale.setScalar(gameRadius);
		this.surfaceScale = group.scale.x;

		this.planetMesh = p.planetMesh;
		this.planetMesh.data = this;
		this.planetMesh.userData.noLabelOcclusion = true;
		this.atmosphere = p.atmosphere;

		group.add(this.settlements);
		group.add(this.facilities);
		this.mesh.add(group);
		planetInstances.add(this);
		applyGridState(this);   // default state 0 (semi-transparent black)

		// per-frame: pump the chunked bakes (reveals the mesh) + drive sun/time.
		// next lights via uSunDir (the game's MeshLambert sun does not light it) —
		// the HOST owns lighting: feed the world-space planet→star direction each
		// frame (planet orbits + spins), and setWorldRotation keeps the shader's
		// lighting normal in sync as the planet rotates. Sun is the system centre;
		// this.mesh.position (orbit pos) and system.position share the map frame.
		this._onFrame = () => {
			const np = this.$next;
			if (!np) return;
			_sunDir.set(
				this.system.position.x - this.mesh.position.x,
				0,
				this.system.position.y - this.mesh.position.z,
			).normalize();
			np.update({ time: performance.now() / 1000, sunDir: _sunDir });
			// XR sliders (settings page): exposure scales the base 0.8, saturation 1 = unchanged; outside XR the base values
			const u = np.uniforms;
			if (u && u.uSaturation) {
				const xr = game.renderer?.xr?.isPresenting;
				u.uExposure.value = xr ? PLANET_EXPOSURE * game.xrSettings.get('xrExposure') : PLANET_EXPOSURE;
				u.uSaturation.value = xr ? game.xrSettings.get('xrSaturation') : 1;
			}
			np.setWorldRotation(np.object3d.getWorldQuaternion(_sunQuat));
		};
		if (typeof game !== 'undefined' && game.onFrame) game.onFrame(this._onFrame);

		// tile data for settlements/borders — next exposes unit-sphere tiles; build
		// the arrays createSettlement expects (same shape as legacy, unit coords —
		// the group scale normalizes them exactly like legacy's NOISE_RADIUS coords).
		const n = p.tileCount;
		const centers = new Array(n), neighbors = new Array(n);
		for (let i = 0; i < n; i++) {
			centers[i] = p.getTileCenter(i).toArray();
			neighbors[i] = p.tileNeighbors(i);
		}
		this.tiles = { centers, drapedCenters: centers, boundaries: p.boundaries, neighbors };

		await this.createSettlements();
		if (typeof window !== 'undefined' && window.__tierLog) window.__tierLog.push({ t: Math.round(performance.now()), event: 'phase:settlements-done', planet: this.planet.name });
		// build() only ENQUEUES the bakes; the mesh stays hidden until they drain.
		// Resolving here made sector-map swap in a planet that was atmosphere + city
		// only for seconds (grey → atmosphere → planet, and again on every tier
		// upgrade). Resolve when the surface is really there: the old version
		// (grey sphere / previous tier) stays on screen until then.
		await p.ready;
		if (typeof window !== 'undefined' && window.__tierLog) window.__tierLog.push({ t: Math.round(performance.now()), event: 'phase:ready', planet: this.planet.name });
		return this;
	}

	// free the next renderer's worker + GPU render-targets and stop the per-frame
	// callback — without this, evicting a planet leaks a Worker + RTs + a live
	// onFrame closure (the game's disposeHierarchy only frees mesh geometry).
	dispose() {
		if (this._onFrame && typeof game !== 'undefined' && game.offFrame) game.offFrame(this._onFrame);
		this._onFrame = null;
		planetInstances.delete(this);
		this.$next?.dispose?.();
		this.$next = null;
		this.$legacy?.dispose?.(); // frees the mesh geometry/materials (atlas is shared, not disposed)
		this.$legacy = null;
	}

	async createSettlements() {
		const settlements = this.planet.settlements;
		if (!settlements) return;
		const iterable = Array.isArray(settlements)
			? settlements
			: Object.values(settlements);
		for (const settlement of iterable) {
			if (!settlement) continue;
			const tileIndex = settlement.planetTileIndex ?? Number(Object.keys(settlements).find(key => settlements[key] === settlement));
			if (Number.isFinite(tileIndex)) await this.createSettlement(settlement, tileIndex);
		}
	}

	async createSettlement(settlement, planetTileIndex) {
		const tilemesh = new Group();
		const settlementMesh = new Settlement();
		tilemesh.add(settlementMesh.mesh);

		const boundaries = this.tiles.boundaries[planetTileIndex];
		const centerArray = this.tiles.drapedCenters[planetTileIndex] || this.tiles.centers[planetTileIndex];
		if (!boundaries || !centerArray) return;
		const center = new Vector3().fromArray(centerArray);

		const actualTileWidth = new Vector3(...boundaries[1]).distanceTo(new Vector3(...boundaries[4]));
		const cityScale = (actualTileWidth / settlementMesh.width) * 0.82;
		const surfaceNormal = center.clone().normalize();

		tilemesh.position.copy(center).addScaledVector(surfaceNormal, actualTileWidth * 0.08);
		tilemesh.quaternion.setFromUnitVectors(SETTLEMENT_UP, surfaceNormal);
		tilemesh.scale.setScalar(cityScale);
		this.settlements.add(tilemesh);

		const settlementBorder = this.createSettlementBorder(settlement, planetTileIndex);
		if (settlementBorder) {
			settlementBorder.translateOnAxis(surfaceNormal, actualTileWidth * 0.02);
			this.settlements.add(settlementBorder);
		}

		if (typeof game !== 'undefined' && game.labels) {
			const label = await createLabel(settlement.name || '');
			const labelId = `${this.planet._id}_settlement_${planetTileIndex}_label`;
			// lifted above the city along the surface normal, so the label does not cover it
			// (user 2026-10-11: "the label is pinned so close to the city that you don't see the city")
			label.userData.planetLocalPosition = center.clone().multiplyScalar((this.surfaceScale || 1) * 1.14);
			label.userData.planetId = this.planet._id;
			// click → the settlement page (sector-map.js onPointerUp)
			label.userData.link = `/${this.planet.sector[0]}/${this.planet.sector[1]}/${this.planet.index}/${planetTileIndex}/${settlement.url || ''}`;
			this.settlementLabels.push(label);
			this.settlementLabelIds.push(labelId);
			this.planet.settlementLabels = this.settlementLabels;
			this.planet.settlementLabelIds = this.settlementLabelIds;
			game.entities.add({
				_id: labelId,
				$type: 'settlementLabel',
				$collisions: false,
				sector: this.planet.sector,
				mesh: label,
			});
			game.labels.add(label);
		}
	}

	createSettlementBorder(settlement, planetTileIndex) {
		const settlementBorder = [];
		const settlementTiles = [planetTileIndex, ...(settlement.tiles || [])];
		for (const tileIndex of settlement.tiles || []) {
			const neighbors = this.tiles.neighbors[tileIndex];
			const boundary = this.tiles.boundaries[tileIndex];
			if (!neighbors || !boundary) continue;
			for (const neighbor of neighbors) {
				if (settlementTiles.includes(neighbor)) continue;
				const neighborBoundary = this.tiles.boundaries[neighbor];
				if (!neighborBoundary) continue;
				const common = neighborBoundary.filter(np => boundary.find(tp => samePoint(tp, np)));
				if (common.length >= 2) settlementBorder.push(common);
			}
		}
		if (!settlementBorder.length) return null;

		function findNextBorderEdge(currentIndex, collection = []) {
			const edge = settlementBorder.splice(currentIndex, 1)[0];
			collection.push(edge);
			if (settlementBorder.length === 0) return collection;
			const distances = [];
			for (const nextIndex in settlementBorder) {
				distances.push({
					d: distanceBetweenBorders(edge[1], settlementBorder[nextIndex][0]),
					i: nextIndex,
					t: 0,
				});
				distances.push({
					d: distanceBetweenBorders(edge[1], settlementBorder[nextIndex][1]),
					i: nextIndex,
					t: 1,
				});
			}
			distances.sort((a, b) => a.d - b.d);
			if (distances[0].t === 1) settlementBorder[distances[0].i].reverse();
			return findNextBorderEdge(distances[0].i, collection);
		}

		const orderedSettlementBorder = findNextBorderEdge(0);
		orderedSettlementBorder.push(orderedSettlementBorder[0]);
		let borderPoints = orderedSettlementBorder.map(point => new Vector3(...point[0]).subScalar(0.0005));
		// next: only the hex CORNERS are draped — a straight edge between two corners cut
		// through any mountain in between (border vanished into the terrain, flickered in VR).
		// Resample every edge and drape each sample with next's CPU height field.
		const np = this.$next;
		if (np?._baseAt && np._K) {
			const draped = [];
			for (let i = 0; i < borderPoints.length - 1; i++) {
				for (let j = 0; j < 8; j++) {
					const v = borderPoints[i].clone().lerp(borderPoints[i + 1], j / 8).normalize();
					draped.push(v.multiplyScalar(1 + (np._baseAt(v) - np._WATER) / np._K + 0.0015));
				}
			}
			draped.push(draped[0].clone());
			borderPoints = draped;
		}
		// HEADSETS: a flat RIBBON on the surface instead of MeshLine. MeshLine offsets
		// its vertices through the projection matrix (three.meshline.js: `normal *=
		// projectionMatrix`), and in XR each eye has its own asymmetric projection →
		// the two eyes get different quads = the "light blue interference" in the
		// border (user, Pico 2026-10-10; the overlapping joint quads also looked
		// "drawn multiple times"). Real geometry is identical in both eyes.
		if (HEADSET_UA.test(typeof navigator !== 'undefined' ? navigator.userAgent || '' : '')) {
			const HW = SETTLEMENT_BORDER_HALF_WIDTH;
			const n = borderPoints.length - 1; // closed loop: last point == first
			const pos = new Float32Array(borderPoints.length * 6);
			const normal = new Vector3(), dir = new Vector3(), side = new Vector3(), tmp = new Vector3();
			for (let i = 0; i < borderPoints.length; i++) {
				const p = borderPoints[i], prev = borderPoints[(i - 1 + n) % n], next = borderPoints[(i + 1) % n];
				normal.copy(p).normalize();
				dir.copy(next).sub(prev).normalize();
				side.crossVectors(dir, normal).normalize();
				tmp.copy(p).addScaledVector(side, HW).toArray(pos, i * 6);
				tmp.copy(p).addScaledVector(side, -HW).toArray(pos, i * 6 + 3);
			}
			const index = [];
			for (let i = 0; i < n; i++) { const a = i * 2; index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
			const geometry = new BufferGeometry();
			geometry.setAttribute('position', new BufferAttribute(pos, 3));
			geometry.setIndex(index);
			geometry.computeVertexNormals();
			return new Mesh(geometry, new MeshBasicMaterial({ color: SETTLEMENT_BORDER_COLOR, side: DoubleSide }));
		}
		const line = new MeshLine();
		line.setPoints(borderPoints);
		// Screen-space width (sizeAttenuation 0) is computed from the 2D canvas
		// `resolution` and the frame's projection matrix. In XR each eye renders
		// with its own asymmetric projection and a different buffer size, so the
		// screen-space offset comes out different per eye: the border appears
		// doubled/shifted sideways and far too wide ("drawn multiple times,
		// forming a coloured area" — Pico, 2026-09-28). Headsets get a WORLD-space
		// width instead (unit-sphere planet units, scaled with the planet group),
		// which is identical in both eyes by construction.
		const headsetUA = HEADSET_UA.test(typeof navigator !== 'undefined' ? navigator.userAgent || '' : '');
		const material = new MeshLineMaterial({
			resolution: new Vector2(
				game?.container?.clientWidth || window.innerWidth,
				game?.container?.clientHeight || window.innerHeight,
			),
			color: SETTLEMENT_BORDER_COLOR,
			lineWidth: headsetUA ? 0.006 : 3,
			sizeAttenuation: headsetUA ? 1 : 0,
			// NO dashes: the old dashArray/dashRatio 0.5 made MeshLine write alpha 0 into the gaps
			// of a NON-transparent material → the transparent canvas showed the page's black
			// background inside the ribbon ("black pixels within the city borders", user
			// 2026-10-16); 7.5 px was "weirdly thick" too.
		});
		// The planet's hex-grid line runs along the SAME tile edges a hair below the border. On
		// phones (16-bit depth, legacy renderer) the black grid line won the depth test against
		// the 3 px border in places → "black pixels within the city borders" (user's phone shot,
		// 2026-10-16). Pull the border towards the camera by a few depth units (precision-
		// independent) and draw it after the planet + grid.
		material.polygonOffset = true;
		material.polygonOffsetFactor = -1;
		material.polygonOffsetUnits = -4;
		const mesh = new Mesh(line, material);
		mesh.renderOrder = 1;
		return mesh;
	}
}

function samePoint(a, b) {
	return Math.abs(a[0] - b[0]) < 0.01
		&& Math.abs(a[1] - b[1]) < 0.01
		&& Math.abs(a[2] - b[2]) < 0.01;
}

function distanceBetweenBorders(a, b) {
	const dx = a[0] - b[0];
	const dy = a[1] - b[1];
	const dz = a[2] - b[2];
	return Math.sqrt(dx * dx + dy * dy + dz * dz);
}
